import {
  IGrouperPreviewResponse,
  IGrouperSaveResponse,
  IGrouperSetsResponse,
} from "types/PayerGrouper/PayerGrouper";

import { api } from ".";

/**
 * CLE-2323 / CLE-2331 / CLE-2566 — RTK Query layer for the rate-tools endpoints. The
 * splitter, expander, and grouper-generate endpoints return binary downloads (ZIP for the
 * splitter, CSV for the other two). We pull diagnostic headers off the response (split
 * file mode, row counts, unmapped service lines, unpriced grouper categories) and return
 * them alongside the blob so the SPA can show a results banner.
 */

export interface ISplitResponse {
  blob: Blob;
  headers: Record<string, string>;
}

/**
 * CLE-2514 — one Specialty Overwrite group. For `specialties` (service lines), each output
 * side emits one row per selected modifier token (tagged in the downloaded MOD column).
 * Global / no-modifier is sent as the token `"GLOBAL"` (never an empty string, which the
 * server would drop). An empty modifier list for a side keeps that side on the default split.
 */
export interface ISpecialtyOverride {
  specialties: string[];
  facilityModifiers: string[];
  nonFacilityModifiers: string[];
}

export interface ISplitRequest {
  file: File;
  includeFacility: boolean;
  includeNonFacility: boolean;
  includeServiceLine: boolean;
  /**
   * CLE-2464 (deprecated, superseded by specialtyOverrides) — service lines for which the
   * non-facility output should prefer the technical component (TC) rate over global.
   */
  tcServiceLines?: string[];
  /**
   * CLE-2514 — Specialty Overwrite groups. Sent as a JSON array; omitted when empty.
   */
  specialtyOverrides?: ISpecialtyOverride[];
}

export interface IExpandResponse {
  blob: Blob;
  headers: Record<string, string>;
}

export interface IExpandRequest {
  baseFile: File;
  rulesFile: File;
  accountId: string;
  payerId: number;
  /**
   * Claim type used to scope the per-payer service-line library lookup
   * (CLE-2323 follow-on). Defaults to PROFESSIONAL on the server when omitted.
   */
  claimType?: "PROFESSIONAL" | "OUTPATIENT" | "INPATIENT";
}

/** Surface 400-JSON errors from the expander (rules-sheet validation) with errors[] preserved. */
export interface IExpandValidationError {
  message: string;
  errors: string[];
}

/** CLE-2566 — grouper-set upload (preview or save). `sheetName` picks a worksheet. */
export interface IGrouperUploadRequest {
  file: File;
  sheetName?: string;
}

export interface IGrouperGenerateRequest {
  grouperSetId: string;
  /** Raw textarea contents — one `category rate` pair per line. Parsed server-side. */
  pricingText: string;
  fileName?: string;
}

export interface IGrouperGenerateResponse {
  blob: Blob;
  headers: Record<string, string>;
}

function collectHeaders(response: Response): Record<string, string> {
  const out: Record<string, string> = {};
  response.headers.forEach((v, k) => {
    out[k] = v;
  });
  return out;
}

/**
 * CLE-2566 — a custom `responseHandler` also runs for error responses, so a blob-returning
 * endpoint would otherwise hand the component a Blob instead of the JSON `{ errors: [] }`
 * body it needs to list the bad pricing lines. Parse JSON on the failure path.
 */
async function blobOrJsonError(response: Response) {
  if (response.ok) {
    return { blob: await response.blob(), headers: collectHeaders(response) };
  }
  try {
    return await response.json();
  } catch {
    return { message: await response.text() };
  }
}

const rateToolsQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    splitRateFile: builder.mutation<ISplitResponse, ISplitRequest>({
      query: ({
        file,
        includeFacility,
        includeNonFacility,
        includeServiceLine,
        tcServiceLines,
        specialtyOverrides,
      }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("includeFacility", String(includeFacility));
        formData.append("includeNonFacility", String(includeNonFacility));
        formData.append("includeServiceLine", String(includeServiceLine));
        if (tcServiceLines && tcServiceLines.length > 0) {
          formData.append("tcServiceLines", JSON.stringify(tcServiceLines));
        }
        if (specialtyOverrides && specialtyOverrides.length > 0) {
          formData.append(
            "specialtyOverrides",
            JSON.stringify(specialtyOverrides)
          );
        }
        return {
          url: "contract-fee-schedules/tools/split",
          method: "POST",
          body: formData,
          responseHandler: async (response: Response) => ({
            blob: await response.blob(),
            headers: collectHeaders(response),
          }),
        };
      },
    }),
    expandRateFile: builder.mutation<IExpandResponse, IExpandRequest>({
      query: ({ baseFile, rulesFile, accountId, payerId, claimType }) => {
        const formData = new FormData();
        formData.append("baseFile", baseFile);
        formData.append("rulesFile", rulesFile);
        formData.append("accountId", accountId);
        formData.append("payerId", String(payerId));
        if (claimType) formData.append("claimType", claimType);
        return {
          url: "contract-fee-schedules/tools/expand",
          method: "POST",
          body: formData,
          responseHandler: async (response: Response) => ({
            blob: await response.blob(),
            headers: collectHeaders(response),
          }),
        };
      },
    }),
    /**
     * CLE-2566 — grouper sets. Preview parses an upload without writing; save is a
     * wipe-and-rewrite keyed on the set name.
     */
    getGrouperSets: builder.query<IGrouperSetsResponse, void>({
      query: () => "contract-fee-schedules/tools/grouper-sets",
      providesTags: ["PayerGrouperSets"],
    }),
    previewGrouperSet: builder.mutation<
      IGrouperPreviewResponse,
      IGrouperUploadRequest
    >({
      query: ({ file, sheetName }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (sheetName) formData.append("sheetName", sheetName);
        return {
          url: "contract-fee-schedules/tools/grouper-sets/preview",
          method: "POST",
          body: formData,
        };
      },
    }),
    saveGrouperSet: builder.mutation<
      IGrouperSaveResponse,
      IGrouperUploadRequest & { name: string; payerId?: number | null }
    >({
      query: ({ file, sheetName, name, payerId }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", name);
        if (sheetName) formData.append("sheetName", sheetName);
        if (payerId != null) formData.append("payerId", String(payerId));
        return {
          url: "contract-fee-schedules/tools/grouper-sets",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["PayerGrouperSets"],
    }),
    deleteGrouperSet: builder.mutation<
      { data: { id: string }; message: string },
      string
    >({
      query: (id) => ({
        url: `contract-fee-schedules/tools/grouper-sets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PayerGrouperSets"],
    }),
    generateGrouperRateFile: builder.mutation<
      IGrouperGenerateResponse,
      IGrouperGenerateRequest
    >({
      query: ({ grouperSetId, pricingText, fileName }) => ({
        url: "contract-fee-schedules/tools/grouper-generate",
        method: "POST",
        body: { grouperSetId, pricingText, fileName },
        responseHandler: blobOrJsonError,
      }),
    }),
  }),
});

export const {
  useSplitRateFileMutation,
  useExpandRateFileMutation,
  useGetGrouperSetsQuery,
  usePreviewGrouperSetMutation,
  useSaveGrouperSetMutation,
  useDeleteGrouperSetMutation,
  useGenerateGrouperRateFileMutation,
} = rateToolsQueries;
